// jsqr y pdfjs-dist se cargan al usarse: pesan más que el resto del arranque junto y solo
// sirven para leer el documento de un gasto. El import del worker es solo su URL.
import trabajadorPdf from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

interface DetectorCodigo {
  detect(fuente: CanvasImageSource): Promise<{ rawValue: string }[]>;
}
declare global {
  interface Window {
    BarcodeDetector?: new (opts?: { formats: string[] }) => DetectorCodigo;
  }
}

/** En un escaneo el QR queda pequeño, así que la página se rasteriza en grande. */
const ESCALA_PDF = 3;
const PAGINAS_MAX = 3;
const TIPOS_ACEPTADOS = ['application/pdf', 'image/png', 'image/jpeg', 'image/webp'];
const TAMANO_MAX = 15 * 1024 * 1024;

type ResultadoLectura =
  | { estado: 'leido'; crudo: string }
  | { estado: 'sin_qr' }
  | { estado: 'rechazado'; motivo: string };

/** Lee el QR de un canvas: primero el detector nativo, y si no está, jsQR sobre los píxeles. */
async function leerCanvas(canvas: HTMLCanvasElement): Promise<string | null> {
  if (typeof window.BarcodeDetector === 'function') {
    try {
      const codigos = await new window.BarcodeDetector({ formats: ['qr_code'] }).detect(canvas);
      const valor = codigos[0]?.rawValue;
      if (valor) return valor;
    } catch {
      // El detector nativo puede fallar por formato no soportado; jsQR responde igual.
    }
  }
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  const imagen = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const { default: jsQR } = await import('jsqr');
  return jsQR(imagen.data, imagen.width, imagen.height, { inversionAttempts: 'attemptBoth' })?.data ?? null;
}

async function leerImagen(archivo: File): Promise<string | null> {
  const bitmap = await createImageBitmap(archivo);
  try {
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    canvas.getContext('2d')?.drawImage(bitmap, 0, 0);
    return await leerCanvas(canvas);
  } finally {
    bitmap.close();
  }
}

async function leerPdf(archivo: File): Promise<string | null> {
  const pdfjs = await import('pdfjs-dist');
  pdfjs.GlobalWorkerOptions.workerSrc = trabajadorPdf;
  const tarea = pdfjs.getDocument({ data: await archivo.arrayBuffer() });
  const documento = await tarea.promise;
  try {
    for (let n = 1; n <= Math.min(documento.numPages, PAGINAS_MAX); n += 1) {
      const pagina = await documento.getPage(n);
      const vista = pagina.getViewport({ scale: ESCALA_PDF });
      const canvas = document.createElement('canvas');
      canvas.width = Math.floor(vista.width);
      canvas.height = Math.floor(vista.height);
      const ctx = canvas.getContext('2d');
      if (!ctx) continue;
      await pagina.render({ canvas, canvasContext: ctx, viewport: vista }).promise;
      const crudo = await leerCanvas(canvas);
      if (crudo) return crudo;
    }
    return null;
  } finally {
    void tarea.destroy();
  }
}

/**
 * Extrae el contenido del QR DIAN de un documento de proveedor en PDF o imagen. Suelen ser escaneos
 * sin capa de texto: se rasteriza la página y se lee el código, sin extraer texto.
 */
export async function leerQrDeArchivo(archivo: File): Promise<ResultadoLectura> {
  if (!TIPOS_ACEPTADOS.includes(archivo.type)) {
    return { estado: 'rechazado', motivo: 'Solo se aceptan archivos PDF, PNG, JPG o WEBP.' };
  }
  if (archivo.size > TAMANO_MAX) {
    return { estado: 'rechazado', motivo: 'El archivo supera los 15 MB.' };
  }
  try {
    const crudo = archivo.type === 'application/pdf' ? await leerPdf(archivo) : await leerImagen(archivo);
    return crudo ? { estado: 'leido', crudo } : { estado: 'sin_qr' };
  } catch {
    return { estado: 'rechazado', motivo: 'No pudimos abrir el archivo. ¿Está completo?' };
  }
}
